import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBets, fetchBetStats } from '../api/client';
import DataTable from '../components/DataTable';
import MetricCard from '../components/MetricCard';
import type { Bet } from '../types';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'filled', label: 'Filled' },
  { value: 'partial', label: 'Partial' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function Bets() {
  const [statusFilter, setStatusFilter] = useState('');
  const [days, setDays] = useState(30);

  const { data: betsData, isLoading } = useQuery({
    queryKey: ['bets', { status: statusFilter, days }],
    queryFn: () => fetchBets({ status: statusFilter || undefined, days }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['bet-stats'],
    queryFn: fetchBetStats,
  });

  const columns = [
    {
      key: 'placedAt',
      header: 'Date',
      render: (bet: Bet) => (
        <span className="text-sm">{formatDate(bet.placedAt)}</span>
      ),
    },
    {
      key: 'market',
      header: 'Market',
      render: (bet: Bet) => (
        <div>
          <div className="font-medium text-sm truncate max-w-xs">
            {bet.polymarketTitle || 'Unknown Market'}
          </div>
        </div>
      ),
    },
    {
      key: 'side',
      header: 'Side',
      render: (bet: Bet) => (
        <span
          className={`badge ${
            bet.side === 'BUY' ? 'badge-success' : 'badge-danger'
          }`}
        >
          {bet.side}
        </span>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      render: (bet: Bet) => (
        <span className="font-mono">${bet.size.toFixed(2)}</span>
      ),
    },
    {
      key: 'price',
      header: 'Entry',
      render: (bet: Bet) => (
        <span className="font-mono">{(bet.entryPrice * 100).toFixed(1)}¢</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (bet: Bet) => (
        <span
          className={`badge ${
            bet.status === 'filled'
              ? 'badge-success'
              : bet.status === 'pending'
              ? 'badge-warning'
              : 'bg-gray-500/20 text-gray-400'
          }`}
        >
          {bet.status}
        </span>
      ),
    },
    {
      key: 'pnl',
      header: 'P&L',
      render: (bet: Bet) => (
        <span
          className={`font-mono font-medium ${
            bet.pnl === null
              ? 'text-gray-400'
              : bet.pnl >= 0
              ? 'text-success'
              : 'text-danger'
          }`}
        >
          {bet.pnl !== null ? formatPnL(bet.pnl) : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Bet Management</h1>
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Total Bets"
            value={statsData.summary.totalBets}
            icon="📊"
          />
          <MetricCard
            label="Win Rate"
            value={`${(statsData.summary.winRate * 100).toFixed(1)}%`}
            icon="🎯"
          />
          <MetricCard
            label="Total P&L"
            value={formatPnL(statsData.summary.totalPnL)}
            trend={statsData.summary.totalPnL >= 0 ? 'up' : 'down'}
            icon="💰"
          />
          <MetricCard
            label="ROI"
            value={`${(statsData.summary.roi * 100).toFixed(2)}%`}
            trend={statsData.summary.roi >= 0 ? 'up' : 'down'}
            icon="📈"
          />
        </div>
      )}

      {/* Open Positions */}
      {betsData?.bets.some((b) => b.status === 'pending') && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Open Positions</h2>
          <DataTable
            columns={columns}
            data={betsData.bets.filter((b) => b.status === 'pending')}
            keyExtractor={(b) => b.id}
            emptyMessage="No open positions"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Bet History */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Bet History</h2>
        <DataTable
          columns={columns}
          data={betsData?.bets || []}
          keyExtractor={(b) => b.id}
          loading={isLoading}
          emptyMessage="No bets found"
        />
      </div>
    </div>
  );
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatPnL(value: number): string {
  const prefix = value >= 0 ? '+$' : '-$';
  return `${prefix}${Math.abs(value).toFixed(2)}`;
}
